import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { admin } from "../../api/client";
import { STUCK_AFTER_MS, type OrderSummary } from "../../api/types";
import { TocaPage } from "../../components/Shell";
import { Empty, ErrorBox, Kpi, Modal, Pill, Spinner } from "../../components/ui";
import { useAsync } from "../../hooks/useAsync";
import { fmt, t } from "../../i18n";
import { dateTimeShort, durationSec, timeHM } from "../../lib/format";
import { brl, pct, usd } from "../../lib/money";
import { OrderActions, exceptionReason } from "./shared";
import { supplierName } from "../Painel";

function compact(v: string): string {
  // "R$ 3.842.000,10" -> "R$ 3,84 mi"
  const n = Number(BigInt(v) / 100n);
  if (n >= 999_500) return `R$ ${(n / 1_000_000).toFixed(2).replace(".", ",")} mi`; // 999.500+ arredonda para "1,00 mi", nunca "1000 mil"
  if (n >= 10_000) return `R$ ${Math.round(n / 1000)} mil`;
  return brl(v);
}

export function TocaFila() {
  const navigate = useNavigate();
  const dash = useAsync(() => admin.dashboard(), []);
  const queue = useAsync(() => admin.orders({ exception: true }), []);
  const runs = useAsync(() => admin.reconciliationRuns(), []);
  const [resolving, setResolving] = useState<OrderSummary | null>(null);
  const items = queue.data?.items ?? [];
  const d = dash.data;
  const lastRun = runs.data?.[0];
  const crit = items.filter((o) => exceptionReason(o).tone === "crit").length;
  const stuck = d?.stuckOnramps;
  const stuckMin = Math.round(STUCK_AFTER_MS / 60_000);

  return (
    <TocaPage title={t.toca.queueTitle}>
      <div className="grid5">
        <Kpi label={t.toca.kpiExceptions} value={queue.data ? items.length : "…"} tone={items.length ? "crit" : undefined} sub={fmt(t.toca.kpiExceptionsSub, { crit, rev: items.length - crit })} />
        <Kpi
          label={t.toca.kpiRecon}
          value={lastRun ? (lastRun.issueCount === 0 ? t.toca.kpiReconOk : fmt(t.toca.kpiReconIssues, { n: lastRun.issueCount })) : runs.loading ? "…" : t.toca.kpiReconNone}
          tone={lastRun ? (lastRun.issueCount === 0 ? "ok" : "crit") : undefined}
          sub={lastRun ? fmt(t.toca.kpiReconSub, { time: timeHM(lastRun.finishedAt ?? lastRun.startedAt), pix: lastRun.pixCount, orders: lastRun.orderCount, exec: lastRun.execCount }) : ""}
        />
        <Kpi
          label={t.toca.kpiTiming}
          value={d ? `${durationSec(d.timing.quoteToFundsP50Sec + d.timing.fundsToSettledP50Sec)} / ${durationSec(d.timing.quoteToFundsP95Sec + d.timing.fundsToSettledP95Sec)}` : "…"}
          sub={d ? fmt(t.toca.kpiTimingSub, { days: 7, n: d.ordersCount }) : ""}
        />
        <Kpi label={t.toca.kpiTpv} value={d ? compact(d.tpvCents) : "…"} sub={d ? fmt(t.toca.kpiTpvSub, { rate: pct(d.effectiveTakeRateBp), revenue: brl(d.revenueCents) }) : ""} />
        <Kpi label={t.toca.kpiStuck} value={d ? (stuck?.count ?? 0) : "…"} tone={stuck?.count ? "crit" : d ? "ok" : undefined} sub={d ? (stuck?.count ? fmt(t.toca.kpiStuckSub, { min: stuckMin }) : t.toca.kpiStuckSubNone) : ""} />
      </div>
      {dash.error ? <ErrorBox error={dash.error} onRetry={dash.reload} /> : null}

      {stuck && stuck.count > 0 && (
        <div className="card banner crit" role="status" style={{ alignItems: "flex-start", flexDirection: "column", gap: 8 }}>
          <b>{t.toca.stuckTitle}</b>
          <span className="ink2">{fmt(t.toca.stuckLead, { min: stuckMin })}</span>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {stuck.items.map((s) => (
              <li key={s.orderId}>
                <Link to={`/toca/pedidos/${s.orderId}`} className="mono" style={{ fontSize: 13 }}>{s.orderId}</Link>
                <span className="muted"> · {fmt(t.toca.stuckItem, { amount: brl(s.totalCents), min: s.minutes })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>{t.toca.queueTitle}</h2>
          <div className="row" style={{ gap: 8 }}>
            <Pill tone="mute">{t.nav.tocaPedidos} {items.length}</Pill>
            <Link to="/toca/acessos" className={`pill ${d?.pendingAccess ? "warn" : "mute"}`} style={{ textDecoration: "none" }}><i aria-hidden="true" />{fmt(t.toca.accessPendingPill, { n: d?.pendingAccess ?? 0 })}</Link>
            <Link to="/toca/kyb" className="pill mute" style={{ textDecoration: "none" }}><i aria-hidden="true" />{t.nav.tocaOnboarding}</Link>
            <Link to="/toca/fornecedores" className="pill mute" style={{ textDecoration: "none" }}><i aria-hidden="true" />{t.nav.tocaFornecedores}</Link>
          </div>
        </div>
        {queue.loading && !queue.data ? (
          <Spinner />
        ) : queue.error ? (
          <div style={{ padding: 16 }}><ErrorBox error={queue.error} onRetry={queue.reload} /></div>
        ) : items.length === 0 ? (
          <Empty body={t.toca.queueEmpty} />
        ) : (
          <>
            <div className="tscroll desk-table">
              <table>
                <thead>
                  <tr>
                    <th>{t.toca.queueColOrder}</th>
                    <th>{t.toca.queueColWho}</th>
                    <th>{t.toca.queueColAmount}</th>
                    <th>{t.toca.queueColReason}</th>
                    <th>{t.toca.queueColSince}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((o) => {
                    const r = exceptionReason(o);
                    return (
                      <tr key={o.id}>
                        <td className="mono" style={{ fontSize: 13 }}>{o.id}</td>
                        <td>
                          <div className="cell2">
                            <b>{supplierName(o)}{o.company?.legalName ? ` · ${o.company.legalName}` : ""}</b>
                            <span>{r.detail}</span>
                          </div>
                        </td>
                        <td className="mono">{usd(o.usdCents)}</td>
                        <td><Pill tone={r.tone}>{r.label}</Pill></td>
                        <td className="muted" style={{ whiteSpace: "nowrap" }}>{dateTimeShort(o.createdAt)}</td>
                        <td className="r">
                          <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                            <Link to={`/toca/pedidos/${o.id}`} className="btn ghost xs">{t.toca.open}</Link>
                            <button type="button" className="btn dark xs" onClick={() => setResolving(o)}>{t.toca.resolve}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mob-list">
              {items.map((o) => {
                const r = exceptionReason(o);
                return (
                  <button type="button" key={o.id} className="item" style={{ background: "none", border: 0, borderBottom: "1px solid var(--surface-2)", textAlign: "left", width: "100%" }} onClick={() => navigate(`/toca/pedidos/${o.id}`)}>
                    <div className="l">
                      <span className="mono" style={{ fontSize: 12 }}>{o.id}</span>
                      <b>{supplierName(o)} · <span className="mono">{usd(o.usdCents)}</span></b>
                      <span>{r.detail}</span>
                    </div>
                    <div className="r"><Pill tone={r.tone}>{r.label}</Pill><small>{dateTimeShort(o.createdAt)}</small></div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <p className="legal">{t.toca.queueFooter}</p>
      {resolving && (
        <Modal title={`${t.toca.resolve} · ${resolving.id}`} onClose={() => setResolving(null)}>
          <div className="stack s8" style={{ fontSize: 13.5 }}>
            <div className="row"><Pill tone={exceptionReason(resolving).tone}>{exceptionReason(resolving).label}</Pill><span className="muted">{supplierName(resolving)} · {usd(resolving.usdCents)}</span></div>
            <p className="ink2">{exceptionReason(resolving).detail}</p>
          </div>
          <OrderActions order={resolving} onDone={() => { setResolving(null); void queue.reload(); }} />
        </Modal>
      )}
    </TocaPage>
  );
}
